import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { FiMail, FiSend, FiX, FiFileText } from 'react-icons/fi';
import toast from 'react-hot-toast';

const EmailComposer = ({ leadId, leadEmail, onClose, onSent }) => {
  const [templates, setTemplates] = useState([]);
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    templateId: ''
  });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      const { data } = await axios.get('/api/crm/email-templates?isActive=true');
      setTemplates(data);
    } catch (error) {
      console.error('Failed to fetch templates');
    }
  };

  const handleTemplateSelect = (templateId) => {
    const template = templates.find(t => t.id === parseInt(templateId));
    if (template) {
      setFormData({
        ...formData,
        templateId,
        subject: template.subject,
        message: template.htmlContent
      });
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!formData.subject || !formData.message) {
      toast.error('Subject and message are required');
      return;
    }

    setSending(true);
    try {
      await axios.post(`/api/crm/leads/${leadId}/email`, formData);
      toast.success('Email sent successfully');
      onSent?.();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-blue-600 text-white p-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <FiMail size={20} />
            <h2 className="text-xl font-semibold">Compose Email</h2>
          </div>
          <button onClick={onClose} className="hover:bg-blue-700 p-1 rounded">
            <FiX size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSend} className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="email"
              value={leadEmail}
              disabled
              className="w-full border rounded-lg px-3 py-2 bg-gray-50"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              <FiFileText className="inline mr-1" />
              Email Template (Optional)
            </label>
            <select
              value={formData.templateId}
              onChange={(e) => handleTemplateSelect(e.target.value)}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
            >
              <option value="">-- Select Template --</option>
              {templates.map(template => (
                <option key={template.id} value={template.id}>
                  {template.templateName} - {template.templateType}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
            <input
              type="text"
              value={formData.subject}
              onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500"
              placeholder="Enter email subject"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Message *</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 min-h-[300px]"
              placeholder="Enter your message..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              You can use HTML formatting in the message
            </p>
          </div>
        </form>

        {/* Footer */}
        <div className="border-t p-4 flex justify-end gap-3 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border rounded-lg hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2 disabled:opacity-50"
          >
            <FiSend size={16} />
            {sending ? 'Sending...' : 'Send Email'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmailComposer;
